// Generated by scripts/build-rules-content.mjs from country-de.json (aligned 9 Oct 2026).
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { CountryGuide } from "../types";

export const GUIDE_GERMANY: CountryGuide = {
  id: "germany",
  code: "de",
  title: "Germany (Wheelabrator, WGG)",
  shortName: "Germany",
  entities: ["wgg"],
  areaRoles: ["de"],
  live: true,
  notLiveNote:
    "Germany is live in shadow mode: I2Q matches items, finds prices and prepares drafts, and sales check, decide and send. Not there yet: no item group mapping (the quotation preview has no sales pool), and I2Q does not read the German replies, so an answered inquiry stays open until someone closes it.",
  cardLine:
    "Live in shadow mode: items matched, prices from the price list or price group, no freight line. No item group mapping and no reply reading yet.",
  notYet: [
    "I2Q does not read the German replies yet, so an answered inquiry stays open in I2Q until someone closes it, and the quality figures for Germany cannot be measured.",
    "No item group mapping: the quotation preview has no sales pool.",
    "Lead times are not available for German lines yet: the line shows no delivery date.",
  ],
  summary:
    "Wheelabrator Germany (WGG) sells spare parts and wear parts, mostly to German customers, often through agents. Germany is a new area in Norican I2Q, live in shadow mode since its item catalogue was loaded and checked. Germany never went live in the old tool (Go Autonomous), mainly because item groups and sales units were not ready. Prices come from the customer's price list or price group, and a line below the margin limit needs a manager's approval. No freight line goes on a German quotation. Norican I2Q runs in shadow mode: it prepares a draft, and sales always checks, decides and sends.",
  howInquiriesArrive: [
    "Customers write to the shared mailbox Wheelabrator.Parts.DE. Many German customers still write to personal addresses or old addresses, because they were never moved to the shared mailbox. Norican I2Q reads a copy of the shared mailbox and picks up a new mail within a few minutes. Nothing is moved, answered or sent. About six to eight colleagues share the mailbox. Most of its mail is newsletters and spam, often only about one real customer mail a day. Inquiries come in German and sometimes English; I2Q writes the reply draft in the language of the inquiry. StrikoWestofen Germany (DES) is a separate company with its own setup and is not part of this area.",
  ],
  specialities: [
    {
      id: "de-area-switch",
      title: "Germany is live in shadow mode, with two gaps",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "The German area is switched on: German mails get matched items and prices like every other area. Two parts are not there yet: the item group mapping and the reading of German replies.",
      why: "Germany could not go live in the old tool because its item groups and sales units were not ready, so I2Q first loads and checks the German catalogue.",
      youSee:
        'Info. German inquiries appear in "To review" with prepared lines. The quotation preview warns that there is no item group mapping, and the backlog says "replies not measured".',
      whatToDo:
        "Check the lines and the prices, then quote in F&O as today. Close the case in I2Q when you answered it, because I2Q does not see your reply.",
      example:
        "A customer in Bavaria asks for 50 blast wheel blades. The case shows the matched blade and its price. You check it, quote in F&O and set the case to done.",
      registryIds: [],
      glossaryIds: [],
      source:
        "backend areas.py (Germany = new area, not core); store/area_switch.py; goa-knowledge.md Part A section 3 Germany and 9.5 (Gantt: DE had no dates)",
    },
    {
      id: "de-mailbox-noise",
      title: "A shared mailbox full of newsletters",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Whether a mail is a real request for a price or a quotation. Newsletters, spam, order confirmations, invoices and delivery questions are counted but set aside.",
      why: "About 90 % of the German shared mailbox was newsletters, and the old tool's users said the real inquiries got lost in the noise.",
      youSee:
        'Info. Real inquiries go to "To review". Everything else is listed under "Not an inquiry" (or as an order) and gets no draft.',
      whatToDo:
        'Work from "To review". If a mail under "Not an inquiry" is an inquiry after all, open it and use "Give feedback".',
      example:
        'A trade fair newsletter and a customer asking "Bitte um Angebot für 4 Schleuderräder" arrive at the same time. Only the customer\'s mail shows up in "To review".',
      registryIds: [],
      glossaryIds: ["scope-inquiries-only"],
      source:
        "goa-knowledge.md Part B section 2 DE / WGG (Mailbox [00:09], [00:17]-[00:25]); docs/scope.md",
    },
    {
      id: "de-what-is-for-me",
      title: "Who handles which inquiry",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Which sales colleague owns an inquiry, based on the customer's sales unit (who is responsible for the customer and who takes the order).",
      why: 'German users asked "what is for me?" because a fully shared inbox without owners did not work for them.',
      youSee:
        "Info. Today you can assign a case by hand. An automatic owner from the customer's sales unit is planned.",
      whatToDo: "Agree in the team who takes which customers and assign the case to that person.",
      example:
        "An inquiry from a customer in Saxony is assigned to the colleague who looks after eastern Germany, so nobody else picks it up twice.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md Part A 2.4 (draft owner), 6.3; backlog S5; transcript GoA US-DE Shared mailbox setup [00:23]-[00:25]",
    },
    {
      id: "de-customer-from-contact",
      title: "Finding the customer from the sender",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "Whether the sender's e-mail address belongs to a contact of a German customer account in F&O. Germany has by far the most contacts with an e-mail address (about 19,700).",
      why: "In the old tool the customer account was right on only about half of the mails, and a known contact e-mail is the safest way to the right account.",
      youSee:
        'Info. The customer card shows the account and the reason, for example "Known sender". A weaker match shows "Suggested, please confirm".',
      whatToDo:
        'Check the reason under the name (Why). If it fits, click "Confirm" with the name on it. If not, click "Not them" or "Find customer" and search. If the contact is missing in F&O, add it there so the next mail is found.',
      example:
        'A buyer at a foundry near Stuttgart writes from the company address, which is a contact on the account. I2Q fills in the customer as "Known sender".',
      registryIds: [],
      glossaryIds: ["customer-finding"],
      source: "goa-knowledge.md Part A 1 (point 2), 2.1 (sender to account, contacts WGG 19,663)",
    },
    {
      id: "de-agents",
      title: "Agents write for their customers",
      status: "planned",
      tone: "info",
      topic: "customer",
      checks:
        "Whether the sender is an agent writing on behalf of an end customer. The quotation is then for the end customer, and in Germany the agent's name goes into the customer reference.",
      why: "About 30 % of European customers deal with Norican through agents, and the old tool could link one agent to only one customer.",
      youSee:
        "Today: if one sender is linked to several accounts, I2Q does not guess and asks you to pick the customer. Planned: a list of agent domains, so I2Q knows an agent at once.",
      whatToDo:
        "Find the end customer in the mail text, pick that account, and put the agent in the customer reference as you do today.",
      example:
        "An agent forwards a request for a shot blast machine part for a customer in Austria. You pick the Austrian end customer's account and enter the agent in the reference field.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      source:
        "goa-knowledge.md Part A 2.1 (agents), 3 Germany (reference holds the agent), 9.3 (agents, four cases)",
    },
    {
      id: "de-uk-customers-quoted-from-germany",
      title: "Some UK inquiries must be quoted from Germany",
      status: "active",
      tone: "warning",
      topic: "customer",
      checks:
        "Whether a customer who writes to the UK mailbox is one of about 28 customers that must be quoted from Wheelabrator Germany. These customers hold both a UK and a German account.",
      why: 'The old tool had a rule "create in WGG" for these customers, because quoting them from the UK put the business in the wrong company.',
      youSee:
        'Warning on the UK case: "This customer is quoted from Wheelabrator Germany, account ...", with the German account number.',
      whatToDo: "When the UK team passes such an inquiry on, quote it on the German account.",
      example:
        "A UK subsidiary of a German car maker asks the UK team for wear parts. The customer is on the list, so the quotation is made on the German account.",
      registryIds: ["gate.uk_wgg"],
      glossaryIds: [],
      modes: {
        "gate.uk_wgg": {
          uk: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.1 (UK mailbox, WGG customer), 3 UK; backlog M10; UK raised in WGG custoner list.xlsx",
    },
    {
      id: "de-portal-customers",
      title: "Customers who buy through their own portal",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks:
        "Whether the customer is on the German portal list (about 30 customers). For each one the list says if quotes, orders or both go through the portal; 11 are listed for quotations.",
      why: "Portal customers expect the quotation in their own purchasing portal, so a normal mail quotation is wrong or lost.",
      youSee:
        'Blocked for a customer listed for quotations: "This customer handles quotations through a supplier portal." Customers listed for orders only are not held on an inquiry.',
      whatToDo: "Quote this customer through their portal, not by mail.",
      example:
        "A large automotive customer sends a request through its supplier portal. The quotation is entered in that portal, not sent by mail.",
      registryIds: ["gate.portal"],
      glossaryIds: ["portal-customers"],
      modes: {
        "gate.portal": {
          wgg: "block",
        },
      },
      source:
        "goa-knowledge.md Part A 2.1 (portal customers, WGG 31 rows); Blocked portal list WGG.xlsx",
    },
    {
      id: "de-competitors-and-sanctions",
      title: "Competitors and sanctioned countries",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks:
        "Whether the sender's domain is on the group list of competitors (about 430 domains) or belongs to a sanctioned country (for example .ru, .by, .ir).",
      why: "Competitors get a higher price or no quotation, and sanctioned countries must not be quoted at all.",
      youSee:
        'Blocked: "The sender\'s mail domain belongs to Russia, which is on the sanctions list." Blocked: "The sender\'s domain is on the competitor list." The case is held and the queue shows this reason first.',
      whatToDo:
        "Do not quote a sanctioned country. For a competitor, decide with your manager whether and at what price to quote.",
      example:
        "A request arrives from a domain that belongs to a competing blast machine maker. The case is marked so that a manager decides the price.",
      registryIds: ["gate.competitor", "gate.sanctioned"],
      glossaryIds: ["competitor-domains", "sanctioned-countries"],
      modes: {
        "gate.competitor": {
          wgg: "block",
        },
        "gate.sanctioned": {
          wgg: "block",
        },
      },
      source: "goa-knowledge.md Part A 1 (point 6), 2.1 (competitor domains, sanctioned countries)",
    },
    {
      id: "de-customer-item-numbers",
      title: "Customers use their own part numbers",
      status: "active",
      tone: "info",
      topic: "items",
      checks:
        "Whether a number in the inquiry is the customer's own part number for one of our items, using the customer number list kept in F&O.",
      why: "About 40 % of German orders use the customer's own number, and the old tool's cross-reference covered only about 1 % of our items.",
      youSee:
        'Info. A line found through the customer\'s number shows "Matched" with our item. A line with no link shows "Needs a check" or "Not evaluated, check by hand".',
      whatToDo:
        "Check the matched item. If the link is missing, pick the item and ask for the customer number to be added in F&O.",
      example:
        'The inquiry says "Pos. 3: 12 Stk. Art.-Nr. K-4711-08". That is the customer\'s number for one of our blast wheel liners, and I2Q shows our item.',
      plannedPart:
        "About 190 more German links from customer text to our item, from the old order reading tool.",
      registryIds: [],
      glossaryIds: ["customer-item-numbers"],
      source:
        "goa-knowledge.md Part A 2.2 (customer item numbers: WGG ~40 %, Lasernet WGG 188), 3 Germany",
    },
    {
      id: "de-old-9105-numbers",
      title: "Old 9105 numbers and their successors",
      status: "active",
      tone: "warning",
      topic: "items",
      checks:
        "Whether an item asked for is replaced by a newer item. Old 9105 series numbers are typical in German inquiries and point to a successor in F&O.",
      why: "The old tool sometimes kept the old item or swapped a valid item for its predecessor, which led to wrong quotations.",
      youSee:
        'Warning: "Line 3: use 654321, it replaces 9105-0123." The line also shows "Phase-out item, replacement" when F&O marks the old item as phased out.',
      whatToDo:
        "Quote the successor item and mention the customer's old number in the line text, so the customer recognises it.",
      example:
        'A customer asks for 9105-0123. F&O lists a successor item. You quote the successor and write "replaces 9105-0123".',
      plannedPart: "Keep the customer's old number on the quotation line.",
      registryIds: ["gate.successor"],
      glossaryIds: ["successor-items"],
      modes: {
        "gate.successor": {
          wgg: "warn",
        },
      },
      source:
        "goa-knowledge.md Part B 2 DE / WGG (OW1 [01:25]-[01:26]); Part A 2.2 (alternatives and successors)",
    },
    {
      id: "de-items-from-czechia",
      title: "Many parts come from the Czech plant",
      status: "active",
      tone: "blocked",
      topic: "items",
      checks:
        "Whether an item that is not in the German catalogue exists in another Norican company, mainly the Czech plant, where many machine parts are made.",
      why: "In the old tool items created by the Czech plant got a default item group (WHE_DISA) that did not fit the German setup.",
      youSee:
        'Blocked: the line shows "Item is in another catalogue, price not selected". You see the item, but it gets no German price.',
      whatToDo:
        "Check whether the item can be sold from Germany. If not, ask for it to be released, then quote it in F&O.",
      example:
        "A customer asks for a motor of an older machine. The part is only in the Czech catalogue. I2Q shows it from there, and you check that it can be sold from Germany.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md Part A 3 Germany (CZ-created items default to WHE_DISA); backend engine/pipeline/resolve.py (cross-company lookup)",
    },
    {
      id: "de-item-signal-codes",
      title: "Items that cannot be quoted as they are",
      status: "active",
      tone: "blocked",
      topic: "items",
      checks:
        "The item's status code in F&O. Only items marked OK can be quoted without a closer look.",
      why: "The old tool quoted phased-out items and failed without a message on new and revised items.",
      youSee:
        'Blocked: "Blocked for sale in F&O" on the line. Warning: "Phase-out item", with the replacement number when F&O has one. Warning for a code that needs a check: "Line 2 has signal code REV, which needs a check before quoting."',
      whatToDo:
        "Ask the product team before you quote an item under revision or a phased-out item without a successor.",
      example:
        'A line matches an item that is being phased out. The line shows "Phase-out item" with its replacement, and you offer the replacement.',
      registryIds: ["gate.signal_review"],
      glossaryIds: ["signal-codes"],
      modes: {
        "gate.signal_review": {
          wgg: "warn",
        },
      },
      source: "goa-knowledge.md Part A 2.2 (signal codes)",
    },
    {
      id: "de-prices",
      title: "Prices come from the price list",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "The price for the customer from F&O: first the customer's own price agreement, else the price group of the customer, valid on the day and for the quantity asked.",
      why: "Prices must come from F&O only, so a draft never shows a price that sales would not give.",
      youSee:
        'Info: the line shows the list price. Blocked: the line shows "No price", and the prepared quotation says "No price was found for ...". The other lines still show.',
      whatToDo:
        "Check the price. If there is no price, get it from purchasing or the product team as today. A discount you give by hand is not known to I2Q.",
      example:
        "A customer with its own price agreement asks for 200 wear plates. The price for that quantity band is filled in. For an item without a price, the line stays empty and you ask for a price.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          wgg: "block",
        },
      },
      source:
        'goa-knowledge.md Part A 2.3 (price source, no price / zero price); help topic "mail-to-quotation"',
    },
    {
      id: "de-margin",
      title: "Margin check",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        "Where F&O has a cost for the item, how much of the price is left after cost (the contribution ratio). It is compared with the margin limit for Germany.",
      why: "The old tool stopped low-margin quotations, and users asked for a step they can get released instead.",
      youSee:
        'Blocked until a manager approves: the line shows "Margin: needs approval". If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held as well. Only colleagues with margin rights see the figures. Everyone else sees the result only.',
      whatToDo:
        'Check the price. If it is right, ask your manager to release it with "Approve price". If the item, price, quantity or customer changes later, the approval must be given again.',
      example:
        'A large order for a standard spare part comes out below the margin limit. The line shows "Margin: needs approval", and your manager releases it.',
      registryIds: ["pricing.margin"],
      glossaryIds: ["contribution-ratio"],
      modes: {
        "pricing.margin": {
          wgg: "block",
        },
      },
      source:
        "backend pricing/thresholds.py (wgg 0.40); goa-knowledge.md Part A 2.3 (contribution ratio gate)",
    },
    {
      id: "de-freight",
      title: "No freight line on German quotations",
      status: "active",
      tone: "info",
      topic: "freight",
      checks:
        "Whether a freight charge belongs on the quotation. For Germany it does not: freight is added later, at invoicing.",
      why: "Wheelabrator Germany asked to keep freight out of automatic quotes and orders, even though a freight table with German values existed.",
      youSee:
        'Info. The preview\'s freight says "Does not apply", with "This entity adds no freight at quote time."',
      whatToDo:
        "Add freight by hand only where you agreed it with the customer, for example an express delivery.",
      example:
        "A customer asks for 10 filter cartridges by tomorrow. The draft has no freight. You add the express charge yourself because the customer asked for overnight delivery.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          wgg: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.5 (freight tiers: WGG does not want freight automation), 9.3 (freight model a: added at invoicing for WGG)",
    },
    {
      id: "de-media",
      title: "Blast media goes its own way",
      status: "active",
      tone: "warning",
      topic: "items",
      checks: "Whether the inquiry contains blast media (item group starting with MEDIA).",
      why: "Germany sells media, but it is often shipped directly from the supplier and carriage works differently, so the old tool kept it out of automation.",
      youSee:
        'Warning on media lines: "Line 2 is in the Media item group ..." The line is still matched and priced.',
      whatToDo: "Check delivery and carriage for media with the supplier before you quote.",
      example:
        "A customer asks for 5 tonnes of steel shot. You check the direct delivery and carriage before you send the price.",
      registryIds: ["gate.media"],
      glossaryIds: ["media-items"],
      modes: {
        "gate.media": {
          wgg: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.2 (media item groups), 3 Germany (Media is sold but kept out)",
    },
    {
      id: "de-sales-pool",
      title: "Sales pool and sales fields are set by hand",
      status: "active",
      tone: "warning",
      topic: "quotation",
      checks:
        "Which sales pool and which sales fields (division, business type, sales product) the quotation gets. In other countries these follow from the item group; for Germany there is no such table.",
      why: "Germany has no agreed table from item group to sales pool, and choosing the pool by line count would break the historic split between pools.",
      youSee:
        'Warning: "Germany (Wheelabrator) has no item group mapping yet, so no sales pool is proposed." Division, business type and sales product come from the customer\'s history, with a note.',
      whatToDo: "Set the sales pool in F&O as you do today.",
      example:
        "A quotation mixes blast wheel parts and filter parts. You choose the pool in F&O the way your team has always split this business.",
      registryIds: ["header.pool"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.pool": {
          wgg: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 1 (point 4), 2.4 (WGG none, open pool decisions), 3 Germany; Part B 2 DE / WGG (DK-agents [00:00]-[00:22])",
    },
    {
      id: "de-sales-unit",
      title: "Sales unit and commissions",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "Which sales unit the customer has: the sales colleague responsible for the customer (printed on the quotation) and the colleague who takes the order.",
      why: "In Germany the sales unit also drives agent commissions and sales targets in Finance, and the old setup had only one German sales unit filled in.",
      youSee:
        "Info: a note says the sales unit, sales responsible and sales taker are not available yet.",
      whatToDo: "Check the sales unit in F&O on every quotation, especially for agent customers.",
      example:
        "An agent's customer is quoted. You check that the sales unit with the agent is on the quotation, so the commission is right.",
      plannedPart: "The sales unit from the customer account.",
      registryIds: ["header.not_available"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.not_available": {
          wgg: "info",
        },
      },
      source:
        "goa-knowledge.md Part A 2.4 (sales unit: WGG 1 row, 01-10), 9.1 (WGG sales units feed agent commissions, #100)",
    },
    {
      id: "de-requisition-reference",
      title: "Requisition and reference fields mean something else in Germany",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks:
        "What goes into the two customer fields on the quotation header. In Germany the requisition field holds the machine type or the inspection date, and the reference field holds the agent.",
      why: "Germany uses these fields differently from the other countries, and a filled-in value from the general rule would overwrite this practice.",
      youSee:
        "Info. The preview fills the two fields the general way: the customer's inquiry number as reference, requisition empty. A German rule is planned.",
      whatToDo: "Fill the two fields in F&O the German way, as today.",
      example:
        'For a customer whose machine is due for inspection, you enter "Inspektion 03/2027" in the requisition and the agent\'s name in the reference.',
      registryIds: [],
      glossaryIds: ["customer-reference-fields"],
      source:
        "goa-knowledge.md Part A 2.5 (customer requisition / reference: WGG differs), 3 Germany",
    },
    {
      id: "de-payment-terms",
      title: "Customers who pay in advance",
      status: "planned",
      tone: "warning",
      topic: "freight",
      checks:
        "The payment terms of the customer account. The German code VOR means payment in advance.",
      why: "A customer who pays in advance needs a pro-forma with freight before the order, which the old tool could not do.",
      youSee:
        'Planned: warning "Payment in advance". Today the payment terms are not shown on the draft.',
      whatToDo: "For a VOR customer, prepare a pro-forma invoice with freight as today.",
      example:
        "A new customer in Poland has payment terms VOR. You send a pro-forma with freight so the customer can pay before shipping.",
      registryIds: [],
      glossaryIds: ["freight"],
      source:
        "goa-knowledge.md Part A 2.5 (payment terms: WGG VOR), 9.3 (prepayment customers manual)",
    },
    {
      id: "de-terms-and-tax",
      title: "Delivery terms, tax and export",
      status: "planned",
      tone: "info",
      topic: "tax",
      checks:
        "Delivery terms, mode of delivery and tax from the customer account. German customers are grouped as Drittland (outside the EU), EU and IC (another Norican company).",
      why: "Tax and export papers depend on whether the customer is in Germany, elsewhere in the EU or outside the EU, so the group must be right on the account.",
      youSee:
        "Info today: quotations to other Norican companies are recognised. Planned: delivery terms and tax on the draft.",
      whatToDo: "Check delivery terms and tax in F&O, especially for customers outside the EU.",
      example:
        "A customer in Switzerland is in the group Drittland. You check that the quotation has no German VAT and the right delivery terms.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md Part A 2.1 (customer groups: WGG Drittland/EU/IC), 2.5 (delivery terms / mode); backend contracts/group_companies.py",
    },
    {
      id: "de-reply-language",
      title: "Reply in the customer's language",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks: "The language of the inquiry. The reply draft is written in the same language.",
      why: "German customers write in German and expect a German answer.",
      youSee:
        "Info. The reply draft is in German for a German inquiry. A mail in another language is shown translated.",
      whatToDo: "Read the draft and adjust the wording before you send it yourself.",
      example:
        'A customer writes "Wir benötigen ein Angebot für 6 Wurfschaufeln". The reply draft starts in German.',
      registryIds: [],
      glossaryIds: ["translation"],
      source: "goa-knowledge.md Part A 2.5 (reply language); Part B 2 DE / WGG (language)",
    },
    {
      id: "de-document-language",
      title: "The quotation itself in German",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The language printed on the quotation: the customer's language if set, else German for Wheelabrator Germany.",
      why: "All companies printed in American English until April 2025, and German customers need a German quotation.",
      youSee:
        'The preview shows the document language: the customer\'s, else German, marked "(company default)".',
      whatToDo:
        "Check the language on the quotation in F&O, especially for customers outside Germany.",
      example:
        "A customer in the Netherlands has English set on its account, so its quotation is printed in English, while a German customer gets German.",
      registryIds: [],
      glossaryIds: ["translation"],
      source:
        "goa-knowledge.md Part A 2.5 (quote document language: WGG should be de), 9.1 (#28); backlog S2",
    },
    {
      id: "de-item-names",
      title: "German item names for foreign customers",
      status: "planned",
      tone: "warning",
      topic: "items",
      checks:
        "Whether the item name is in German while the customer is not German. Many items have a second, English item text that could be used instead.",
      why: "Default item names are often not in English, and foreign customers then get a quotation they cannot read.",
      youSee: "Planned: a warning on lines with a German name for a foreign customer.",
      whatToDo: "Use the English item text for customers outside Germany.",
      example:
        'A customer in Sweden gets a line called "Schleuderradschaufel". You change it to the English text "blast wheel blade".',
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md Part A 9.1 (item text language, #13, #36); backlog S15",
    },
    {
      id: "de-urgent",
      title: "Urgent and machine down",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks:
        'Words in the inquiry that mean the customer\'s machine is down or the matter is urgent, in German too (for example "Maschinenstillstand" or "dringend").',
      why: "The old tool had keyword lists for English, French and Spanish only, so German urgent mails were not marked.",
      youSee: "Planned: info, an urgent tag on the case.",
      whatToDo: "Read the subject and first lines yourself until the German tag exists.",
      example:
        '"Anlage steht, brauchen dringend 2 Turbinenschaufeln" should be picked first, before routine requests.',
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md Part A 2.5 (urgency tags: no DE list); backlog S8",
    },
    {
      id: "de-credit-block",
      title: "Customer on credit block",
      status: "planned",
      tone: "warning",
      topic: "customer",
      checks:
        "Whether the customer account is blocked by Finance and whether that block matters for a quotation or only for orders and invoices.",
      why: "In the old tool a block for invoices also stopped quotations, and the notice was only in English.",
      youSee: "Planned: a warning that the account is on block. A quotation is still allowed.",
      whatToDo:
        "Quote as usual and tell the customer that Finance must clear the account before an order can be confirmed.",
      example:
        "A customer with overdue invoices asks for a price. You send the quotation and point out the open invoices.",
      registryIds: [],
      glossaryIds: ["prospect-and-credit-block"],
      source: "goa-knowledge.md Part A 2.1 (credit block), 9.1 (#86, #93); backlog S17",
    },
    {
      id: "de-human-sends",
      title: "You always decide and send",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Nothing. This is how I2Q works: it prepares a draft and a quotation preview, and nothing leaves Norican without you.",
      why: "In the old tool a person always sent the quotation, and I2Q keeps that rule.",
      youSee:
        'Info. "Prepare quotation" shows the quotation as it would be created in F&O. Creating it in F&O is switched off.',
      whatToDo:
        "Copy what helps, create the quotation in F&O and send it from the shared mailbox as today.",
      example:
        "You check the draft for a German foundry, correct one price and create the quotation in F&O yourself.",
      registryIds: [],
      glossaryIds: ["nothing-is-sent"],
      source:
        "goa-knowledge.md Part B 3 (sending; DE-WC [00:11]-[00:13]); docs/scope.md (shadow mode)",
    },
  ],
};
